/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { SnackbarRef } from './snackbar.ref';
import { Subject } from 'rxjs';
import { THEME_TOKEN, Theme } from '../types/theme.types';
import { componentStylesByTheme } from '../utils/theme.utils';
import { snackbarStylesSAG, snackbarStylesSHS } from './snackbar.styles';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';

@Component({
  selector: 'sag-ui-snackbar',
  template: `<div
    class="flex fixed top-0 left-0 bottom-0 right-0 pointer-events-none"
    (click)="handleOverlayClick()"
  >
    <div
      [class]="snackbarClasses"
      (click)="handleSnackbarClick($event)"
      [ngStyle]="snackbarStyles"
      [@slideInOut]="{
        value: state,
        params: { start: animationStart, end: animationEnd }
      }"
    >
      <div class="flex items-center">
        <div
          class="flex items-center justify-center p-2"
          *ngIf="icon || type !== 'plain'"
        >
          <span [class]="iconClasses"></span>
        </div>
        <div class="flex flex-col">
          <div class="font-bold">{{ title }}</div>
          <div class="text-sm">{{ description }}</div>
        </div>
      </div>
    </div>
  </div>`,
  animations: [
    trigger('slideInOut', [
      state('in', style({ transform: '{{ start }}' }), {
        params: { start: '' },
      }),
      state('out', style({ transform: '{{ end }}' }), {
        params: { end: '' },
      }),
      transition('in => out', animate('300ms ease-in')),
      transition('out => in', animate('300ms ease-in')),
    ]),
  ],
})
export class SnackbarComponent implements AfterViewInit {
  @Input() icon = '';
  @Input() iconClass = '';
  @Input() type: 'info' | 'success' | 'warning' | 'error' | 'plain' = 'info';
  @Input() title: string | undefined;
  @Input() description: string | undefined;
  @Input() duration = 5000;
  @Input() position:
    | 'top-left'
    | 'top'
    | 'top-right'
    | 'bottom-left'
    | 'bottom'
    | 'bottom-right' = 'bottom';

  private readonly _onClose = new Subject<any>();
  private closeTimeout: ReturnType<typeof setTimeout> | undefined;

  public onClose = this._onClose.asObservable();
  public state: 'out' | 'in' = 'out';

  constructor(
    @Inject(THEME_TOKEN) private readonly theme: Theme,
    private cd: ChangeDetectorRef,
    private snackbarRef: SnackbarRef<any>
  ) {}

  ngAfterViewInit() {
    this.cd.detectChanges();
    setTimeout(() => {
      this.open();
    }, 200);
  }

  protected handleOverlayClick() {
    this.snackbarRef.close();
  }

  protected handleSnackbarClick(evt: MouseEvent) {
    evt.stopPropagation();
  }

  protected get snackbarClasses() {
    const styles = componentStylesByTheme(
      this.theme,
      snackbarStylesSAG,
      snackbarStylesSHS
    );

    return styles();
  }

  protected get snackbarStyles() {
    switch (this.position) {
      case 'top-left':
        return {
          top: '1rem',
          left: '1rem',
        };
      case 'top':
        return {
          top: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'top-right':
        return {
          top: '1rem',
          right: '1rem',
        };
      case 'bottom-left':
        return {
          bottom: '1rem',
          left: '1rem',
        };
      case 'bottom':
        return {
          bottom: '1rem',
          left: '50%',
          transform: 'translateX(-50%)',
        };
      case 'bottom-right':
        return {
          bottom: '1rem',
          right: '1rem',
        };
    }
  }

  protected get animationStart() {
    switch (this.position) {
      case 'top-left':
        return 'translateX(0)';
      case 'top':
        return 'translateY(0) translateX(-50%)';
      case 'top-right':
        return 'translateX(0)';
      case 'bottom-left':
        return 'translateX(0)';
      case 'bottom':
        return 'translateY(0) translateX(-50%)';
      case 'bottom-right':
        return 'translateX(0)';
    }
  }

  protected get animationEnd() {
    switch (this.position) {
      case 'top-left':
        return 'translateX(-150%)';
      case 'top':
        return 'translateY(-150%) translateX(-50%)';
      case 'top-right':
        return 'translateX(150%)';
      case 'bottom-left':
        return 'translateX(-150%)';
      case 'bottom':
        return 'translateY(150%) translateX(-50%)';
      case 'bottom-right':
        return 'translateX(150%)';
    }
  }

  protected get iconClasses() {
    if (this.icon) {
      return `icon-${this.icon} ${this.iconClass} text-xl`;
    }

    const iconsMap = {
      sag: {
        info: 'icon-Information text-[#007EB1] dark:text-[#00BEDC] text-xl',
        success: 'icon-Success text-[#01893A] dark:text-[#01D65A] text-xl',
        warning: 'icon-Warning text-[#E96401] dark:text-[#FF9000] text-xl',
        error: 'icon-Error text-[#D72339] dark:text-[#FF2640] text-xl',
        plain: '',
      },
      shs: {
        info: 'icon-Information text-[#007EB1] dark:text-[#00BEDC] text-xl',
        success: 'icon-Success text-[#01893A] dark:text-[#01D65A] text-xl',
        warning: 'icon-Warning text-[#E96401] dark:text-[#FF9000] text-xl',
        error: 'icon-Error text-[#D72339] dark:text-[#FF2640] text-xl',
        plain: '',
      },
    };

    return iconsMap[this.theme][this.type];
  }

  private open() {
    this.state = 'in';

    this.closeTimeout = setTimeout(() => {
      this.close();
    }, this.duration);
  }

  public close() {
    this.state = 'out';

    clearTimeout(this.closeTimeout);
    setTimeout(() => {
      this._onClose.next({});
      this._onClose.complete();
    }, 400);
  }
}
